import {
  Box,
  Divider,
  Typography,
} from '@mui/material'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { useState } from 'react'
import { supabase } from '~/supabase-client'
import { useCurrentUser } from '~/common/use-current-user'
import { useNavigate } from 'react-router-dom'
import { AddFarmModalPage1 } from './farm-modal-page-1'
import { AddFarmModalPage2 } from './farm-modal-page-2'
import { AddFarmContextProvider } from './context/add-farm-context-provider'
import { useAddFarmContext, useAddFarmContextDispatch, resetState } from './context/add-farm-context'
import { cacheManager } from '~/api/cache-manager'

const ModalHeader = () => {
  return (
    <>
      <Typography id="modal-modal-title" variant="h4">
        Create Farm
      </Typography>

      <Divider sx={{ pb: '0.5rem' }} variant='fullWidth' />
    </>
  )
}

export const AddFarmModal = ({ open, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  return (
    <ClosingAwareModal
      open={open}
      onClose={onClose}
      loading={isSubmitting}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        py: 4,
      }}
    >
      <AddFarmContextProvider>
        <AddFarmModalContent onClose={onClose} onSubmitStart={() => setIsSubmitting(true)} />
      </AddFarmContextProvider>
    </ClosingAwareModal>
  )
}

const AddFarmModalContent = ({ onClose, onSubmitStart }) => {
  const navigate = useNavigate()
  const { currentUser } = useCurrentUser()
  const [page, setPage] = useState(1)

  const {
    name,
    lat,
    lng,
    zoom,
    bounds,
    zone,
    lfd,
    ffd,
    gdd5,
    gdd10,
    avgMinTemp,
    avgMaxTemp,
    absMinTemp,
    absMaxTemp,
    avgDtr,
    avgFfw,
    minFfw,
    avgOptimalDays,
    avgHeatDays32,
    avgHeatDays38,
    avgTropNights,
    avgChillDays,
    stdLfd,
    avgDailyGdd5, avgDailyGdd10,
    gdd5Series, gdd10Series,
    stdFfd,
    fsRisk,
  } = useAddFarmContext()
  const dispatch = useAddFarmContextDispatch()

  const _onClose = () => {
    setPage(1)
    dispatch(resetState())
    onClose && onClose()
  }

  const onSubmit = async () => {
    onSubmitStart()
    const boundsArray = [
      bounds.getWest(),  // minLng
      bounds.getSouth(), // minLat
      bounds.getEast(),  // maxLng
      bounds.getNorth(), // maxLat
    ]
    const body = {
      lat, lng, zoom,
      bounds: boundsArray,
      name, userId: currentUser.id,
      zone, lfd, ffd, gdd5, gdd10,
      avgMinTemp, avgMaxTemp,
      absMinTemp, absMaxTemp,
      avgDtr, avgFfw, minFfw,
      avgOptimalDays, avgHeatDays32, avgHeatDays38,
      avgTropNights, avgChillDays,
      stdLfd, stdFfd, fsRisk,
      avgDailyGdd5, avgDailyGdd10,
      gdd5Series, gdd10Series,
    }

    console.log("Saving to Supabase...", body)
    const { data: newFarm } = await supabase.functions.invoke('add-farm', { body })

    // invalidate all caches, essentially a new login once a new farm is created
    cacheManager.invalidateAll()

    // Navigate to new farm - Dashboard will derive activeOrgs from URL
    navigate(`/dashboard?farmId=${newFarm.id}`)

    _onClose()
  }

  return (
    <>
      {page === 1 ? (
        <AddFarmModalPage1
          header={<ModalHeader />}
          onClose={_onClose}
          onNext={() => setPage(2)}
        />
      ) : (
        <AddFarmModalPage2
          header={<ModalHeader />}
          onBack={() => setPage(1)}
          onSubmit={onSubmit}
        />
      )}
    </>
  )
}
